import { useCallback, useState } from 'react';
import { formatNumber } from '@nextgen-web-framework/core';
import { FAIcon, useTranslation, Theme, IconStyle } from '@nextgen-web-framework/all';
import styles from './BonusPerformance.module.scss';
import { BonusPerformanceProps } from './types/propsTypes';

const BonusPerformance = ({
  personalPv,
  personalBv,
  bonusPercent,
  currentGpv,
  performanceBracket,
  maxBonusPercent,
}: BonusPerformanceProps) => {
  const t = useTranslation();
  const gpvText = `${t('web.bonusPerformance.GPV')}`;
  const bonusLevelPercentageText = `${t('web.bonusPerformance.bonusLevel.desc')}`;
  const bonusLevelQualifiedText = `${t('web.bonusPerformance.bonusLevel.qualified')}`;
  const personalPVText = `${t('web.bonusPerformance.personalPV')}`;
  const personalBVText = `${t('web.bonusPerformance.personalBV')}`;

  let progress = Math.min((currentGpv / performanceBracket.maxGpv) * 100, 100);
  const qualified = bonusPercent >= maxBonusPercent ? true : false;
  if (qualified) {
    progress = 100;
  }

  const progressBarWidth = `${progress ? progress : 0.5}%`;
  const [alignBarText, setAlignBarText] = useState(`${progress}%`);
  const [barTextClass, setBarTextClass] = useState('');
  const [progressCaret, setProgressCaret] = useState(progress);
  const extraSpaceBesideTextInPerc = 3;
  const maxCaretProgess = 97;
  const minCaretProgess = 3;
  const extraSpaceMinMaxCaret = 3;
  const extraCaretPosition = 14;

  // Adding or removing extra space beside progress bar text if its to the left or right
  const barTextRef = useCallback(
    (node: HTMLSpanElement) => {
      let additionalClass = '';
      const bigProgress = 95;
      const smallProgress = 21;
      const widthTwoLines = 55;
      const widthOneLine = 85;

      if (node !== null && progress >= 50) {
        additionalClass = 'bonus-performance-progress-bar-caret-long-bar';

        const parentWidth = node.parentElement?.getBoundingClientRect().width || 0;
        let nodeWidth = node.getBoundingClientRect().width;
        const caretPosition = (progress / 100) * parentWidth;
        if (nodeWidth > caretPosition) {
          nodeWidth = caretPosition + extraCaretPosition;
        }

        if (progress > bigProgress) {
          nodeWidth = (nodeWidth * widthOneLine) / widthTwoLines + extraCaretPosition;
          additionalClass = 'bonus-performance-progress-bar-caret-long-space-bar';
        }
        setBarTextClass(additionalClass);
        let calcBarTextLeft = `calc(${progress - extraSpaceBesideTextInPerc}% - ${nodeWidth}px)`;

        if (progress >= maxCaretProgess) {
          setProgressCaret(maxCaretProgess);
          calcBarTextLeft = `calc(${progress - extraSpaceBesideTextInPerc + extraSpaceMinMaxCaret}% - ${nodeWidth}px)`;
        }
        setAlignBarText(calcBarTextLeft);
      } else {
        if (progress < smallProgress) {
          additionalClass = 'bonus-performance-progress-bar-caret-long-space';
        }
        setBarTextClass(additionalClass);
        let calcBarTextLeft = progress + extraSpaceBesideTextInPerc;
        if (progress <= minCaretProgess) {
          setProgressCaret(minCaretProgess);
          calcBarTextLeft = progress + extraSpaceBesideTextInPerc + extraSpaceMinMaxCaret;
        }
        setAlignBarText(`${calcBarTextLeft}%`);
      }
    },
    [progress],
  );

  const formatDecimal = (number: number) => number.toLocaleString(undefined, { minimumFractionDigits: 2 });

  const renderCaretNText = () => (
    <div className={styles['bonus-performance-progress-bar-caret-container']}>
      <div className={styles['bonus-performance-progress-bar-caret']} style={{ left: `${progressCaret}%` }}>
        <FAIcon iconName={'caret-down'} theme={Theme.PURPLE} iconStyle={IconStyle.SOLID} className="fa-3x" />
      </div>
      <span
        ref={barTextRef}
        className={`${styles['bonus-performance-progress-bar-caret-text']} ${styles[barTextClass]}`}
        style={{ left: alignBarText }}
      >
        <span className={styles['bonus-performance-progress-bar-caret-text-current']}>{formatDecimal(currentGpv)}</span>
        {performanceBracket.maxGpv ? (
          <span className={styles['bonus-performance-progress-bar-caret-text-slash']}>/</span>
        ) : (
          <></>
        )}
        <span className={styles['bonus-performance-progress-bar-caret-text-target']}>{`${formatNumber(
          performanceBracket.maxGpv,
        )} ${gpvText}`}</span>
      </span>
    </div>
  );

  const renderBarMarks = () => (
    <div className={styles['bonus-performance-progress-bar-marks']}>
      <div className={styles['bonus-performance-progress-bar-marks-min']}>
        <span className={styles['bonus-performance-progress-bar-marks-percentage']}>
          {performanceBracket.minPercent}%
        </span>
        <span
          className={`${styles['bonus-performance-progress-bar-marks-value']} ${styles['bonus-performance-progress-bar-marks-value-left']}`}
        >
          {`${formatNumber(performanceBracket.minGpv)} ${gpvText}`}
        </span>
      </div>
      <div className={styles['bonus-performance-progress-bar-marks-max']}>
        <span
          className={`${styles['bonus-performance-progress-bar-marks-value']} ${styles['bonus-performance-progress-bar-marks-value-right']}`}
        >
          {`${formatNumber(performanceBracket.maxGpv)} ${gpvText}`}
        </span>
        <span className={styles['bonus-performance-progress-bar-marks-percentage']}>
          {performanceBracket.maxPercent}%
        </span>
      </div>
    </div>
  );

  const renderProgressBar = () => (
    <div className={styles['bonus-performance-progress-bar-container']}>
      {renderCaretNText()}
      <div className={styles['bonus-performance-progress-bar']}>
        <div className={styles['bonus-performance-progress-bar-filled']} style={{ width: progressBarWidth }}></div>
      </div>
      {renderBarMarks()}
    </div>
  );
  return (
    <div className={styles['bonus-performance-container']} data-testid="bonus-performance-container-testId">
      <div className={styles['bonus-performance-metrics-container']}>
        <div className={styles['bonus-performance-metrics-status']}>
          <div className={styles['bonus-performance-metrics-status-value']}>
            {`${formatDecimal(currentGpv)} ${gpvText}`}
          </div>
          <div className={styles['bonus-performance-metrics-status-text']}>
            <div className={styles['bonus-performance-metrics-status-text-icon']}>
              {qualified ? (
                <FAIcon iconName={'shield-check'} theme={Theme.SUCCESS} iconStyle={IconStyle.SOLID} />
              ) : (
                <FAIcon iconName={'arrow-trend-up'} theme={Theme.PURPLE} />
              )}
            </div>
            {qualified ? (
              <div className={styles['bonus-performance-metrics-status-text-desc-qualified']}>
                {bonusLevelQualifiedText}
              </div>
            ) : (
              <div className={styles['bonus-performance-metrics-status-text-desc']}>
                {bonusLevelPercentageText.replace(/{bonusPercent}/g, formatNumber(bonusPercent))}
              </div>
            )}
          </div>
        </div>
        {renderProgressBar()}
      </div>
      <div className={styles['bonus-performance-pvbv-container']}>
        <div className={styles['bonus-performance-pvbv-box']}>
          <div className={styles['bonus-performance-pvbv-detail']}>
            <div className={styles['bonus-performance-pvbv-title']}>{personalPVText}</div>
            <div className={styles['bonus-performance-pvbv-data']}>{formatDecimal(personalPv)}</div>
          </div>
        </div>
        <div className={styles['bonus-performance-pvbv-box']}>
          <div className={styles['bonus-performance-pvbv-detail']}>
            <div className={styles['bonus-performance-pvbv-title']}>{personalBVText}</div>
            <div className={styles['bonus-performance-pvbv-data']}>{formatDecimal(personalBv)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BonusPerformance;
